import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { IconService } from "app/bundles/requests/shared/services/icon.service";
import { MapService } from "app/bundles/requests/shared/services/map.service";
import * as _ from "lodash-es";
import { EventApi } from "../shared/api-services/event-api/event-api.models";
import { RequestApi } from "../shared/api-services/request-api/request-api.models";
import { CollapseItemsEnum } from "../shared/models/collapseItem";
import { EventRequestById } from "../shared/models/event";
import {
  IServiceRequestSummary,
  IWorkorderOrServiceRequestSummary,
  IWorkorderOrServiceRequestDetail,
  RequestSourceTypeEnum,
} from "../shared/models/request";
import { CommentService } from "../shared/services/comment.service";
import { RequestService } from "../shared/services/request.service";
import { StatusService } from "../shared/services/status.service";
import { UtilService } from "../shared/services/util.service";
import { Observable, map, of, reduce } from "rxjs";

interface ImageCarrousel {
  previewImageSrc: string;
  alt?: string;
  title?: string;
}

@Component({
  selector: "app-view-request",
  templateUrl: "./view-request.component.html",
  styleUrls: ["./view-request.component.scss"],
})
export class ViewRequestComponent implements OnInit {
  public requestDetail: IWorkorderOrServiceRequestDetail;
  public requestSummary: IWorkorderOrServiceRequestSummary;
  public events$: Observable<EventRequestById[]>;
  public delayDays = 0;
  public requestSourceTypeEnum = RequestSourceTypeEnum;
  public clientImgAltMessage: string;
  public citizenImages: ImageCarrousel[] = [];
  public foremanImages: ImageCarrousel[] = [];
  public hasDelayOver1Day = false;
  public hasDelayWithin1Day = false;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private requestApiService: RequestApi,
    private eventApiService: EventApi,
    private statusService: StatusService,
    private commentService: CommentService,
    private utilService: UtilService,
    public iconService: IconService,
    private mapService: MapService,
    private requestService: RequestService,
  ) {}

  ngOnInit() {
    this.route.data.subscribe(
      (data: { request: IWorkorderOrServiceRequestDetail }) => {
        this.requestDetail = data.request;
        this.requestSummary = this.requestDetail.requestSummary;

        // prepare data that need to calculate
        if (this.requestDetail.estimatedCompletionDate) {
          this.delayDays = this.utilService.getDelay(
            this.requestDetail.estimatedCompletionDate,
          );
        }

        if (this.delayDays) {
          this.hasDelayOver1Day = this.utilService.hasDelayOver1Day(
            this.delayDays,
          );
          this.hasDelayWithin1Day = this.utilService.hasDelayWithin1Day(
            this.delayDays,
          );
        }
        // sort clients by creation date
        if (this.requestDetail.clients && this.requestDetail.clients.length) {
          this.requestDetail.clients.sort(
            (first, second) =>
              new Date(first.creationDatetime).getTime() -
              new Date(second.creationDatetime).getTime(),
          );
        }

        // build static map
        this.mapService.buildStaticMap(this.requestDetail);

        const serviceRequestDetail =
          this.requestDetail.source === RequestSourceTypeEnum.serviceRequest
            ? this.requestDetail
            : this.requestDetail.serviceRequestDetail;

        this.citizenImages = serviceRequestDetail?.citizenMedias?.map?.(
          (m) => ({
            alt: m.description,
            previewImageSrc: m.url,
          }),
        );

        this.foremanImages = this.requestDetail.foremanMedias?.map?.((m) => ({
          alt: m.description,
          previewImageSrc: m.url,
        }));

        // get all things (event) connected to this workorder
        if (
          this.requestDetail.source === RequestSourceTypeEnum.workorderRequest
        ) {
          const workorderRequestDetail = this.requestDetail;
          this.events$ = this.eventApiService
            .getEventsByWorkorderId(workorderRequestDetail.id)
            .pipe(
              map((events) => {
                return events?.map?.((event) => ({
                  ...event,
                  statusCode:
                    this.statusService.getAlreadyFetchedPredefinedTextByCode(
                      event.statusCode,
                    ),
                  reason:
                    this.commentService.getAlreadyFetchedPredefinedTextByCode(
                      event.reason,
                    ),
                }));
              }),
              // sort
              map((originalEvents) => {
                const events = [...originalEvents];
                return events.sort(
                  (first, second) =>
                    new Date(second.creationDate).getTime() -
                    new Date(first.creationDate).getTime(),
                );
              }),
              map(
                (sortedEvents) =>
                  sortedEvents?.map?.((e) => ({
                    ...e,
                    statusName:
                      this.statusService.getAlreadyFetchedPredefinedTextByCode(
                        e.statusCode,
                      ),
                  })),
              ),
              map((sortedEventsWithStatusName) => {
                const serviceRequestEvents =
                  workorderRequestDetail.serviceRequestDetail?.events || [];
                return [
                  ...serviceRequestEvents.filter((serviceRequestEvent) =>
                    // Because serviceRequestEvent created after are duplicates of those already
                    // in workorderRequest
                    this.limitToThoseCreatedBeforeWorkorderRequest(
                      serviceRequestEvent,
                    ),
                  ),
                  ...sortedEventsWithStatusName,
                ];
              }),
            );

          return;
        }

        if (
          !(
            this.requestDetail.source ===
            this.requestSourceTypeEnum.serviceRequest
          )
        ) {
          // We just finished handling workorderRequest source and returned
          // At this point, this should be ONLY a serviceRequest source
          // Otherwise, we should throw as this is a unrecognized source
          throw Error(`Source '${this.requestDetail["source"]}' non reconnue`);
        }

        // populate events for serviceRequest
        this.events$ = of(this.requestDetail.events);
      },
    );
  }

  public editRequest(id: number, action?: CollapseItemsEnum) {
    if (id) {
      void this.router.navigate(["/edit-request", this.requestDetail.id], {
        state: {
          requestSummary: this.requestSummary,
          actionFromMenu: action,
        },
      });
    }
  }

  public closeWorkorder(id: number) {
    this.editRequest(id, CollapseItemsEnum.closeWorkorder);
  }

  public backToRequestList(): void {
    void this.router.navigate(["list"]);
  }

  private setImgAltMessage(url) {
    // place to add others alt messages.
    return url && /^http:\/\//g.test(url)
      ? "components.sheet.canNotDisplayPhoto"
      : "";
  }

  private limitToThoseCreatedBeforeWorkorderRequest(
    e: EventRequestById,
  ): boolean {
    if (this.requestDetail.source !== RequestSourceTypeEnum.workorderRequest) {
      return false;
    }
    return (
      e.creationDate.valueOf() <
      this.requestDetail.workorderRequestCreationDatetime.valueOf()
    );
  }
}
